// Version 2.0 - System IO States View (Not Hardware Registry)
// This component shows the system's understanding of IO states from MQTT
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { io, Socket } from 'socket.io-client';
import { useDatabaseContext } from '../contexts/DatabaseContext';
import { usePreferences } from '../contexts/PreferencesContext';
import { getValidStationsForConfig, getStationRangeName } from '../utils/station-config';

interface Line {
  id: string;
  linename: string;
  hardware_id: number;
  current_mode: string;
  system_state: string;
}

interface InfeedState {
  configured: boolean;
  hardware_uuid?: string;
  mqtt_status?: string;
  last_update?: string;
  device_name?: string;
  message?: string;
  states?: {
    infeed_conveyor: string;
    buffer_hopper: string;
    weigh_hopper: string;
    estop: string;
    line_status: string;
    line_production_status?: string;  // Line production status
    calibration_mode?: string;  // Calibration mode (ON/OFF)
    calibration_step?: string;  // Calibration step (IDLE/ZERO_SET/SPAN_SET)
    // Calibration indicators
    red_light?: string;
    yellow_light?: string;
    green_light?: string;
    calibration_button?: string;
    stations: {
      [key: number]: {
        product_sensor: string;
        arm: string;
      }
    }
  };
  weight_scale?: {
    weight: number; // in grams from hardware
    adc_raw: number;
    state: string;
    zero_adc?: number;
    calibration_adc?: number;
    calibration_state?: string;
    excitation_voltage?: number;
    excitation_current?: number;
  }
}

interface OutfeedDevice {
  hardware_uuid?: string;
  device_name: string;
  station_range: string;
  station_config?: number; // Add station config field for proper mapping
  mqtt_status: string;
  last_update: string;
  states: {
    active_button: string;
    red_light: string;
    yellow_light: string;
    green_light: string;
    pocket_door_1: string;
    pocket_door_2: string;
    pocket_door_3: string;
    pocket_door_4: string;
    weigh_hopper_door: string;
    line_status: string;
  };
  weight_scale?: {
    weight: number; // in grams from hardware
    adc_raw: number;
    state: string;
    zero_adc?: number;
    calibration_adc?: number;
    calibration_state?: string;
    excitation_voltage?: number;
    excitation_current?: number;
  }
}

interface OutfeedState {
  configured: boolean;
  devices?: OutfeedDevice[];
  message?: string;
}

interface IOState {
  line_info: Line;
  infeed: InfeedState;
  outfeed: OutfeedState;
}

interface IOSystemResponse {
  timestamp: string;
  lines: Line[];
  io_states: { [lineId: string]: IOState };
}

const IODiagnostics: React.FC = () => {
  const { apiUrl } = useDatabaseContext();
  const [systemData, setSystemData] = useState<IOSystemResponse | null>(null);
  const [selectedLineIndex, setSelectedLineIndex] = useState(0);
  const [selectedDeviceTab, setSelectedDeviceTab] = useState<'infeed' | 'outfeed'>('infeed');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const fetchSystemStatesRef = useRef<() => void>();
  const { weightUnit } = usePreferences(); // Use system preference for weight unit
  const [activeStationTab, setActiveStationTab] = useState<{[deviceIndex: number]: number}>({});  // Per device station tab
  
  // Helper function to convert weight from grams
  const convertWeight = (grams: number): string => {
    if (weightUnit === 'LBS') {
      const lbs = grams / 453.592;
      return lbs.toFixed(2) + ' LBS';
    } else if (weightUnit === 'KGS') {
      const kg = grams / 1000;
      return kg.toFixed(2) + ' KG';
    } else {
      // Default to LBS if preference not set
      const lbs = grams / 453.592;
      return lbs.toFixed(2) + ' LBS';
    }
  };

  // Calibration handler functions for outfeed stations
  const handleCalibrationStart = async (hardwareUuid: string, stationNum: number) => {
    try {
      console.log(`Starting calibration for station ${stationNum} on device ${hardwareUuid}`);
      
      // Send MQTT command to start calibration for specific station
      const response = await axios.post(`${apiUrl}/mqtt/station-command`, {
        hardwareUuid,
        stationNum,
        command: 'CalibrationMode',
        value: 'START'
      });
      
      if (response.data.message === 'success') {
        console.log(`✅ Calibration started for station ${stationNum}`);
        // The UI will update via WebSocket
      }
    } catch (error) {
      console.error(`Failed to start calibration for station ${stationNum}:`, error);
    }
  };

  const handleSetZero = async (hardwareUuid: string, stationNum: number) => {
    try {
      console.log(`Setting zero for station ${stationNum} on device ${hardwareUuid}`);
      
      // Send MQTT command for SetZero
      const response = await axios.post(`${apiUrl}/mqtt/station-command`, {
        hardwareUuid,
        stationNum,
        command: 'SetZero',
        value: 'ON'
      });
      
      if (response.data.message === 'success') {
        console.log(`✅ Zero set for station ${stationNum}`);
      }
    } catch (error) {
      console.error(`Failed to set zero for station ${stationNum}:`, error);
    }
  };

  const handleSetSpan = async (hardwareUuid: string, stationNum: number) => {
    try {
      console.log(`Setting span for station ${stationNum} on device ${hardwareUuid}`);
      
      // Send MQTT command for SetSpan
      const response = await axios.post(`${apiUrl}/mqtt/station-command`, {
        hardwareUuid,
        stationNum,
        command: 'SetSpan',
        value: 'ON'
      });
      
      if (response.data.message === 'success') {
        console.log(`✅ Span set for station ${stationNum}`);
      }
    } catch (error) {
      console.error(`Failed to set span for station ${stationNum}:`, error);
    }
  };

  const handleCalibrationEnd = async (hardwareUuid: string, stationNum: number) => {
    try {
      console.log(`Ending calibration for station ${stationNum} on device ${hardwareUuid}`);
      
      // Send MQTT command to end calibration for specific station
      const response = await axios.post(`${apiUrl}/mqtt/station-command`, {
        hardwareUuid,
        stationNum,
        command: 'CalibrationMode',
        value: 'END'
      });
      
      if (response.data.message === 'success') {
        console.log(`✅ Calibration ended for station ${stationNum}`);
      }
    } catch (error) {
      console.error(`Failed to end calibration for station ${stationNum}:`, error);
    }
  };

  // Fetch system IO states
  const fetchSystemStates = async () => {
    try {
      console.log('[IO Diagnostics v2] Fetching from:', `${apiUrl}/io-system-states`);
      const response = await axios.get(`${apiUrl}/io-system-states`);
      console.log('[IO Diagnostics v2] Response:', response.data);
      
      // Merge with existing state instead of replacing to preserve WebSocket updates
      setSystemData(prevData => {
        if (!prevData) return response.data;
        
        // Smart merge: preserve WebSocket updates that are newer
        const merged = { ...response.data };
        
        // If we have existing io_states with WebSocket updates, preserve them
        if (prevData.io_states && merged.io_states) {
          Object.keys(prevData.io_states).forEach(lineId => {
            if (merged.io_states[lineId] && prevData.io_states[lineId]) {
              // Check if the existing data has more recent WebSocket updates
              const prevInfeed = prevData.io_states[lineId].infeed;
              const newInfeed = merged.io_states[lineId].infeed;
              
              if (prevInfeed && newInfeed) {
                // If the previous update is newer, keep it
                const prevTime = new Date(prevInfeed.last_update || 0).getTime();
                const newTime = new Date(newInfeed.last_update || 0).getTime();
                
                if (prevTime > newTime) {
                  // Keep the WebSocket-updated states
                  merged.io_states[lineId].infeed.states = {
                    ...newInfeed.states,
                    ...prevInfeed.states
                  };
                }
              }
              
              // Also handle outfeed devices merge
              const prevOutfeed = prevData.io_states[lineId].outfeed;
              const newOutfeed = merged.io_states[lineId].outfeed;
              
              if (prevOutfeed && newOutfeed && prevOutfeed.devices && newOutfeed.devices) {
                // Merge outfeed devices preserving WebSocket updates
                newOutfeed.devices.forEach((newDevice, index) => {
                  const prevDevice = prevOutfeed.devices.find(d => d.hardware_uuid === newDevice.hardware_uuid);
                  if (prevDevice) {
                    // Preserve last_update if the previous one is newer
                    const prevTime = new Date(prevDevice.last_update || 0).getTime();
                    const newTime = new Date(newDevice.last_update || 0).getTime();
                    
                    if (prevDevice.last_update && (prevTime > newTime || !newDevice.last_update)) {
                      // Keep the previous last_update if it exists and is newer or if new one is missing
                      newDevice.last_update = prevDevice.last_update;
                    }
                    
                    // Preserve WebSocket-updated station states if newer
                    if (prevDevice.station_states && prevTime > newTime) {
                      newDevice.station_states = {
                        ...newDevice.station_states,
                        ...prevDevice.station_states
                      };
                    }
                  }
                });
              }
            }
          });
        }
        
        return merged;
      });
      
      setLastUpdate(new Date());
      setError(null);
    } catch (err: any) {
      console.error('[IO Diagnostics v2] Failed to fetch system IO states:', err);
      setError(`Failed to fetch system states: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Store fetchSystemStates in ref for WebSocket handlers
  useEffect(() => {
    fetchSystemStatesRef.current = fetchSystemStates;
  }, [apiUrl]);

  useEffect(() => {
    // Initial fetch
    fetchSystemStates();
    
    // Connect to WebSocket for real-time updates
    // Remove /api from the URL for Socket.IO connection
    const socketUrl = apiUrl.replace('/api', '');
    console.log('[IO Diagnostics] 🔌 Connecting to WebSocket:', socketUrl);
    socketRef.current = io(socketUrl, {
      transports: ['websocket', 'polling'],  // Allow fallback to polling
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });
    
    socketRef.current.on('connect', () => {
      console.log('[IO Diagnostics] ✅ WebSocket connected successfully');
      console.log('[IO Diagnostics] Socket ID:', socketRef.current?.id);
      setIsConnected(true);
    });
    
    socketRef.current.on('disconnect', () => {
      console.log('[IO Diagnostics] ❌ WebSocket disconnected');
      setIsConnected(false);
    });
    
    socketRef.current.on('connect_error', (error: any) => {
      console.error('[IO Diagnostics] 🔴 WebSocket connection error:', error.message);
    });
    
    socketRef.current.on('reconnecting', (attemptNumber: number) => {
      console.log(`[IO Diagnostics] 🔄 Reconnecting... attempt ${attemptNumber}`);
    });
    
    // Debug: Catch ALL events to see what's being received
    socketRef.current.onAny((eventName: string, ...args: any[]) => {
      if (!['ping', 'pong'].includes(eventName)) {
        console.log(`[IO Diagnostics] 🎯 Received event "${eventName}":`, args);
      }
    });

    // Listen for real-time IO state updates
    socketRef.current.on('io-state-update', (update: any) => {
      console.log('[IO Diagnostics] Received real-time update:', update);
      
      
      // Update the system data with the new metrics
      setSystemData(prevData => {
        if (!prevData) return prevData;
        
        // Find the line in the current data
        const lineId = prevData.lines.find(l => l.hardware_id === update.lineNumber)?.id;
        if (!lineId) return prevData;
        
        const updatedData = { ...prevData };
        const ioState = updatedData.io_states[lineId];
        
        if (!ioState) return prevData;
        
        // Update the appropriate device type
        if (update.deviceType === 'INFEED' && ioState.infeed.states) {
          // Only update if this is the correct infeed device
          if (ioState.infeed.hardware_uuid === update.hardwareUUID) {
            // Update infeed states
            ioState.infeed.last_update = update.timestamp;
            ioState.infeed.mqtt_status = 'RECEIVING';
            
            // Map metrics to states
            const metrics = update.metrics;
            ioState.infeed.states.infeed_conveyor = metrics.InfeedConveyorState || ioState.infeed.states.infeed_conveyor;
            ioState.infeed.states.buffer_hopper = metrics.BufferHopperState || ioState.infeed.states.buffer_hopper;
            ioState.infeed.states.weigh_hopper = metrics.WeighHopperState || ioState.infeed.states.weigh_hopper;
            ioState.infeed.states.estop = metrics.ESTOPState || ioState.infeed.states.estop;
            ioState.infeed.states.line_status = metrics.LineProductionStatus || ioState.infeed.states.line_status;
            ioState.infeed.states.line_production_status = metrics.LineProductionStatus || ioState.infeed.states.line_production_status;
            ioState.infeed.states.calibration_mode = metrics.CalibrationMode || ioState.infeed.states.calibration_mode;
            ioState.infeed.states.calibration_step = metrics.CalibrationStep || ioState.infeed.states.calibration_step;
            
            // Update station states
            for (let i = 1; i <= 10; i++) {
              if (metrics[`Station${i}ProductSensor`]) {
                ioState.infeed.states.stations[i] = {
                  product_sensor: metrics[`Station${i}ProductSensor`],
                  arm: metrics[`Station${i}Arm`] || ioState.infeed.states.stations[i]?.arm || 'UNKNOWN'
                };
              }
            }
          }
        } else if (update.deviceType === 'OUTFEED' && ioState.outfeed.devices) {
          // Update outfeed device states - match by hardware UUID for accurate updates
          const device = ioState.outfeed.devices.find(d => {
            // Match by hardware UUID to ensure we update the correct device
            return d.hardware_uuid === update.hardwareUUID;
          });
          
          if (device) {
            // Only update timestamp if one is provided
            if (update.timestamp) {
              device.last_update = update.timestamp;
            }
            device.mqtt_status = 'RECEIVING';
            
            // Map metrics to states
            const metrics = update.metrics;
            device.states.active_button = metrics.StationActiveButton || device.states.active_button;
            device.states.red_light = metrics.StationRedLight || device.states.red_light;
            device.states.yellow_light = metrics.StationYellowLight || device.states.yellow_light;
            device.states.green_light = metrics.StationGreenLight || device.states.green_light;
            device.states.pocket_door_1 = metrics.StationPocketDoor1 || device.states.pocket_door_1;
            device.states.pocket_door_2 = metrics.StationPocketDoor2 || device.states.pocket_door_2;
            device.states.pocket_door_3 = metrics.StationPocketDoor3 || device.states.pocket_door_3;
            device.states.pocket_door_4 = metrics.StationPocketDoor4 || device.states.pocket_door_4;
            device.states.weigh_hopper_door = metrics.StationWeighHopperDoor || device.states.weigh_hopper_door;
            device.states.call_for_infeed_button = metrics.CallForInfeedButton || device.states.call_for_infeed_button;
            device.states.call_for_infeed_blue_light = metrics.CallForInfeedBlueLight || device.states.call_for_infeed_blue_light;
            device.states.line_status = metrics.LineProductionStatus || device.states.line_status;
          }
        }
        
        updatedData.timestamp = update.timestamp;
        return updatedData;
      });
      
      setLastUpdate(new Date());
    });
    
    // Listen for hardware status updates (line mode changes)
    socketRef.current.on('hardware-update', (update: any) => {
      console.log('[IO Diagnostics] 📨 Received hardware-update event:', {
        action: update.action,
        lineId: update.lineId,
        hasStates: !!update.states,
        timestamp: new Date().toISOString()
      });
      
      // Handle infeed state updates specifically
      if (update.action === 'infeed-state-update' && update.states) {
        console.log('[IO Diagnostics] 🔄 Processing infeed state update:', {
          lineId: update.lineId,
          allStateKeys: Object.keys(update.states),
          states: update.states,
          redLight: update.states.red_light,
          yellowLight: update.states.yellow_light,
          greenLight: update.states.green_light,
          bufferHopper: update.states.buffer_hopper_state,
          weighHopper: update.states.weigh_hopper_state,
          conveyorState: update.states.infeed_conveyor  // Log conveyor state explicitly
        });
        
        // Update infeed states directly without full refresh
        setSystemData(prevData => {
          if (!prevData || !update.lineId) {
            console.log('[IO Diagnostics] ⚠️ Cannot update - no prevData or lineId');
            return prevData;
          }
          
          const newData = { ...prevData };
          if (newData.io_states[update.lineId] && newData.io_states[update.lineId].infeed) {
            console.log('[IO Diagnostics] ✅ Updating infeed states for line:', update.lineId);
            
            // Update infeed states with the new data
            newData.io_states[update.lineId].infeed.states = {
              ...newData.io_states[update.lineId].infeed.states,
              ...update.states
            };
            
            // Update the last update timestamp
            newData.io_states[update.lineId].infeed.last_update = new Date().toISOString();
          } else {
            console.log('[IO Diagnostics] ⚠️ Line or infeed not found:', update.lineId);
          }
          
          setLastUpdate(new Date());
          return newData;
        });
      } else if (update.action === 'outfeed-state-update' && update.stations) {
        // Handle outfeed state updates specifically
        console.log('[IO Diagnostics] 🔄 Processing outfeed state update:', {
          hardwareUUID: update.hardwareUUID,
          lineId: update.lineId,
          stationCount: Object.keys(update.stations).length,
          stations: update.stations
        });
        
        // Update outfeed states directly without full refresh
        setSystemData(prevData => {
          if (!prevData || !update.lineId) {
            console.log('[IO Diagnostics] ⚠️ Cannot update - no prevData or lineId');
            return prevData;
          }
          
          const newData = { ...prevData };
          if (newData.io_states[update.lineId] && newData.io_states[update.lineId].outfeed?.devices) {
            // Find the device by hardware UUID
            const deviceIndex = newData.io_states[update.lineId].outfeed.devices.findIndex(
              (d: any) => d.hardware_uuid === update.hardwareUUID
            );
            
            if (deviceIndex !== -1) {
              console.log('[IO Diagnostics] ✅ Updating outfeed device:', update.hardwareUUID);
              
              // Update station states for this device
              for (const [stationKey, stationStates] of Object.entries(update.stations)) {
                const stationNum = stationKey.replace('station_', '');
                if (newData.io_states[update.lineId].outfeed.devices[deviceIndex].station_states?.[stationNum]) {
                  console.log(`[IO Diagnostics] Updating station ${stationNum} status from ${newData.io_states[update.lineId].outfeed.devices[deviceIndex].station_states[stationNum].status} to ${(stationStates as any).status}`);
                  newData.io_states[update.lineId].outfeed.devices[deviceIndex].station_states[stationNum] = {
                    ...newData.io_states[update.lineId].outfeed.devices[deviceIndex].station_states[stationNum],
                    ...(stationStates as any)
                  };
                }
              }
              
              // Update timestamp
              newData.io_states[update.lineId].outfeed.devices[deviceIndex].last_update = update.timestamp || new Date().toISOString();
            } else {
              console.log('[IO Diagnostics] ⚠️ Device not found:', update.hardwareUUID);
            }
          } else {
            console.log('[IO Diagnostics] ⚠️ Line or outfeed not found:', update.lineId);
          }
          
          setLastUpdate(new Date());
          return newData;
        });
      } else {
        console.log('[IO Diagnostics] 🔄 Other hardware update - refreshing entire state');
        // For other hardware updates, refresh the entire state
        if (fetchSystemStatesRef.current) {
          fetchSystemStatesRef.current();
        }
      }
    });
    
    // Listen for per-station weight scale updates
    socketRef.current.on('station-weight-update', (update: any) => {
      console.log('[IO Diagnostics] Received station-weight-update:', update);
      
      // Update the per-station weight scale data in the current state
      setSystemData(prevData => {
        if (!prevData) return prevData;
        
        const updatedData = { ...prevData };
        
        // Find the device by hardware_uuid and update its per-station weight scale data
        Object.values(updatedData.io_states).forEach((ioState: any) => {
          // Check outfeed devices (only outfeed has per-station data)
          if (ioState.outfeed && ioState.outfeed.devices) {
            ioState.outfeed.devices.forEach((device: any) => {
              if (device.hardware_uuid === update.hardware_uuid) {
                if (!device.station_weights) {
                  device.station_weights = {};
                }
                device.station_weights[update.station_number] = {
                  weight: update.weight,
                  adc_raw: update.adc_raw,
                  state: update.state
                };
                // Only update timestamp if provided
                if (update.timestamp) {
                  device.last_update = update.timestamp;
                }
              }
            });
          }
        });
        
        updatedData.timestamp = update.timestamp;
        return updatedData;
      });
      
      setLastUpdate(new Date());
    });
    
    // Listen for NEW CONSOLIDATED device updates (replaces individual station events)
    socketRef.current.on('device-update-consolidated', (update: any) => {
      try {
        console.log('🌟 [IO Diagnostics] Received CONSOLIDATED device update:', update);
        
        // Process consolidated station updates efficiently
        if (update && update.stations && Object.keys(update.stations).length > 0) {
          setSystemData((prevData: any) => {
            if (!prevData) return prevData;
            
            try {
              // CRITICAL: Deep clone to ensure React detects changes
              const updatedData = JSON.parse(JSON.stringify(prevData));
              
              // Find the line that contains this hardware UUID
              let targetLineId: string | null = null;
              for (const [lineId, ioState] of Object.entries(updatedData.io_states || {})) {
                const state = ioState as any;
                if (update.deviceType === 'OUTFEED' && state.outfeed?.devices) {
                  const hasDevice = state.outfeed.devices.some((d: any) => d.hardware_uuid === update.hardwareUUID);
                  if (hasDevice) {
                    targetLineId = lineId;
                    break;
                  }
                } else if (update.deviceType === 'INFEED' && state.infeed?.hardware_uuid === update.hardwareUUID) {
                  targetLineId = lineId;
                  break;
                }
              }
              
              if (!targetLineId) {
                console.warn('[IO Diagnostics] Could not find line for hardware UUID:', update.hardwareUUID);
                return prevData;
              }
              
              console.log(`[IO Diagnostics] Updating line ${targetLineId} with consolidated data`);
              const lineData = updatedData.io_states?.[targetLineId];
              
              if (lineData) {
                if (update.deviceType === 'OUTFEED' && lineData.outfeed?.devices) {
                  // Update outfeed station states
                  const deviceIndex = lineData.outfeed.devices.findIndex((d: any) => d.hardware_uuid === update.hardwareUUID);
                  if (deviceIndex !== -1 && lineData.outfeed.devices[deviceIndex].station_states) {
                    Object.entries(update.stations).forEach(([stationNum, stationData]: [string, any]) => {
                      if (lineData.outfeed.devices[deviceIndex].station_states[stationNum] && stationData?.states) {
                        console.log(`[IO Diagnostics] Updating station ${stationNum} status from ${lineData.outfeed.devices[deviceIndex].station_states[stationNum].status} to ${stationData.states.status}`);
                        // Create new station state object
                        lineData.outfeed.devices[deviceIndex].station_states[stationNum] = {
                          ...lineData.outfeed.devices[deviceIndex].station_states[stationNum],
                          ...stationData.states,
                          // Ensure status is explicitly set
                          status: stationData.states.status || lineData.outfeed.devices[deviceIndex].station_states[stationNum].status
                        };
                      }
                    });
                  }
                } else if (update.deviceType === 'INFEED' && lineData.infeed) {
                  // Update infeed states if needed
                  if (update.infeedStates) {
                    console.log(`[IO Diagnostics] Updating infeed states`);
                    lineData.infeed.states = {
                      ...lineData.infeed.states,
                      ...update.infeedStates
                    };
                  }
                }
              }
              
              updatedData.timestamp = update.timestamp || new Date().toISOString();
              console.log('[IO Diagnostics] Returning updated data with new reference');
              return updatedData;
            } catch (innerErr) {
              console.error('[IO Diagnostics] Error updating system data:', innerErr);
              return prevData;
            }
          });
          
          setLastUpdate(new Date());
        }
      } catch (err) {
        console.error('[IO Diagnostics] Error processing consolidated update:', err);
      }
    });
    
    // LEGACY: Keep station-state-update for backward compatibility (reduced priority)
    socketRef.current.on('station-state-update', (update: any) => {
      console.log('⚠️ [IO Diagnostics] Received LEGACY station-state-update:', update);
      
      
      // Update the per-station machine states in the current state
      setSystemData(prevData => {
        if (!prevData) return prevData;
        
        const updatedData = { ...prevData };
        
        // Find the device by hardware_uuid and update its per-station states
        Object.values(updatedData.io_states).forEach((ioState: any) => {
          // Check outfeed devices (only outfeed has per-station data)
          if (ioState.outfeed && ioState.outfeed.devices) {
            ioState.outfeed.devices.forEach((device: any) => {
              if (device.hardware_uuid === update.hardware_uuid) {
                if (!device.station_states) {
                  device.station_states = {};
                }
                device.station_states[update.station_number] = update.states;
                // Only update timestamp if provided
                if (update.timestamp) {
                  device.last_update = update.timestamp;
                }
                
                // TESTING: Log the actual UI state change for Station 1
                if (update.station_number === '1') {
                }
              }
            });
          }
        });
        
        updatedData.timestamp = update.timestamp;
        return updatedData;
      });
      
      setLastUpdate(new Date());
    });
    
    // Listen for weight scale updates (legacy - kept for backward compatibility)
    socketRef.current.on('weight-update', (update: any) => {
      console.log('[IO Diagnostics] Received weight-update (legacy):', update);
      
      // Update the weight scale data in the current state
      setSystemData(prevData => {
        if (!prevData) return prevData;
        
        const updatedData = { ...prevData };
        
        // Find the device by hardware_uuid and update its weight scale data
        Object.values(updatedData.io_states).forEach((ioState: any) => {
          // Check infeed device
          if (ioState.infeed && ioState.infeed.hardware_uuid === update.hardware_uuid) {
            if (!ioState.infeed.weight_scale) {
              ioState.infeed.weight_scale = {};
            }
            ioState.infeed.weight_scale.weight = update.scale_weight;
            ioState.infeed.weight_scale.adc_raw = update.scale_adc_raw;
            ioState.infeed.weight_scale.state = update.scale_state;
            ioState.infeed.last_update = update.timestamp;
          }
          
          // Check outfeed devices
          if (ioState.outfeed && ioState.outfeed.devices) {
            ioState.outfeed.devices.forEach((device: any) => {
              if (device.hardware_uuid === update.hardware_uuid) {
                if (!device.weight_scale) {
                  device.weight_scale = {};
                }
                device.weight_scale.weight = update.scale_weight;
                device.weight_scale.adc_raw = update.scale_adc_raw;
                device.weight_scale.state = update.scale_state;
                // Only update timestamp if provided
                if (update.timestamp) {
                  device.last_update = update.timestamp;
                }
              }
            });
          }
        });
        
        updatedData.timestamp = update.timestamp;
        return updatedData;
      });
      
      setLastUpdate(new Date());
    });
    
    // Listen for Call For Infeed Product updates
    socketRef.current.on('call-for-infeed-update', (update: any) => {
      console.log('[IO Diagnostics] Received call-for-infeed-update:', update);
      
      // Update the Call For Infeed states in the current state
      setSystemData(prevData => {
        if (!prevData) return prevData;
        
        const updatedData = { ...prevData };
        
        // Update outfeed devices with Call For Infeed states
        if (updatedData.outfeed?.devices) {
          updatedData.outfeed.devices.forEach(device => {
            if (device.hardware_uuid === update.hardware_uuid) {
              if (!device.states) {
                device.states = {};
              }
              device.states.call_for_infeed_button = update.button_state;
              device.states.call_for_infeed_blue_light = update.light_state;
              // Only update timestamp if provided
              if (update.timestamp) {
                device.last_update = update.timestamp;
              }
            }
          });
        }
        
        updatedData.timestamp = update.timestamp;
        return updatedData;
      });
      
      setLastUpdate(new Date());
    });
    
    // Fallback polling every 60 seconds in case WebSocket fails
    // Reduced frequency since WebSocket handles real-time updates
    const fallbackInterval = setInterval(() => {
      // Only poll if WebSocket is disconnected or as a periodic sync
      if (fetchSystemStatesRef.current) {
        console.log('[IO Diagnostics] Periodic sync poll (60s interval)');
        fetchSystemStatesRef.current();
      }
    }, 60000);
    
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
      clearInterval(fallbackInterval);
    };
  }, [apiUrl]); // Depend on apiUrl instead of empty array

  const getStatusColor = (status: string | undefined) => {
    if (!status) return 'text-gray-400';
    switch (status.toUpperCase()) {
      case 'RECEIVING':
      case 'ON':
      case 'OPEN':
      case 'PRODUCTION':
        return 'text-green-600';
      case 'STALE':
        return 'text-yellow-600';
      case 'NOT_RECEIVING':
      case 'OFF':
      case 'CLOSED':
      case 'STOP':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusBadgeColor = (status: string | undefined) => {
    if (!status) return 'bg-gray-100 text-gray-600';
    switch (status.toUpperCase()) {
      case 'RECEIVING':
        return 'bg-green-100 text-green-800';
      case 'STALE':
        return 'bg-yellow-100 text-yellow-800';
      case 'NOT_RECEIVING':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const formatLastSeen = (timestamp: string | undefined) => {
    console.log('[formatLastSeen] Input timestamp:', timestamp, 'Type:', typeof timestamp);
    if (!timestamp) {
      console.log('[formatLastSeen] No timestamp, returning Never');
      return 'Never';
    }
    const date = new Date(timestamp);
    console.log('[formatLastSeen] Parsed date:', date, 'Is valid:', !isNaN(date.getTime()));
    if (isNaN(date.getTime())) {
      console.log('[formatLastSeen] Invalid date, returning Invalid Date');
      return 'Invalid Date';
    }
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    
    if (diffSeconds < 60) return `${diffSeconds}s ago`;
    if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`;
    if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`;
    return date.toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <div className="text-sm text-red-800">{error}</div>
      </div>
    );
  }

  if (!systemData || systemData.lines.length === 0) {
    return (
      <div className="rounded-md bg-yellow-50 p-4">
        <div className="text-sm text-yellow-800">No production lines configured in the system.</div>
      </div>
    );
  }

  const selectedLine = systemData.lines[selectedLineIndex];
  const selectedIOState = selectedLine ? systemData.io_states[selectedLine.id] : null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xl font-semibold text-slate-900">System IO</h2>
          <div className="flex items-center space-x-4 text-sm text-slate-500">
            <span>Last Update: {lastUpdate?.toLocaleTimeString() || 'Never'}</span>
            <div className="flex items-center space-x-1">
              <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
              <span>{isConnected ? 'Live (WebSocket)' : 'Disconnected'}</span>
            </div>
          </div>
        </div>
        <p className="text-sm text-slate-600">
          View the system's current understanding of IO states from received MQTT messages.
        </p>
      </div>

      {/* Line Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200">
        <div className="border-b border-slate-200">
          <div className="flex space-x-1 px-4 pt-4 overflow-x-auto">
            {systemData.lines.map((line, index) => (
              <button
                key={line.id}
                onClick={() => setSelectedLineIndex(index)}
                className={`px-4 py-2 text-sm font-medium rounded-t-md transition-colors whitespace-nowrap ${
                  selectedLineIndex === index
                    ? 'bg-white border-t border-l border-r border-slate-200 text-indigo-600 -mb-px'
                    : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
                }`}
              >
                {line.linename}
                <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                  line.system_state === 'PRODUCTION' ? 'bg-green-100 text-green-800' :
                  line.system_state === 'CALIBRATION-ACTIVE' ? 'bg-yellow-100 text-yellow-800' :
                  line.system_state === 'STOP' ? 'bg-red-100 text-red-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {line.system_state}
                </span>
              </button>
            ))}
          </div>
        </div>

        {selectedIOState && (
          <>
            {/* Device Type Tabs */}
            <div className="border-b border-slate-200">
              <div className="flex space-x-4 px-6 pt-4">
                <button
                  onClick={() => setSelectedDeviceTab('infeed')}
                  className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                    selectedDeviceTab === 'infeed'
                      ? 'text-indigo-600 border-indigo-600'
                      : 'text-slate-500 border-transparent hover:text-slate-700'
                  }`}
                >
                  Infeed IO States
                </button>
                <button
                  onClick={() => setSelectedDeviceTab('outfeed')}
                  className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                    selectedDeviceTab === 'outfeed'
                      ? 'text-indigo-600 border-indigo-600'
                      : 'text-slate-500 border-transparent hover:text-slate-700'
                  }`}
                >
                  Outfeed IO States
                </button>
              </div>
            </div>

            {/* IO State Content */}
            <div className="p-6">
              {selectedDeviceTab === 'infeed' ? (
                // Infeed States
                <div>
                  {selectedIOState.infeed.configured ? (
                    <div className="space-y-4">
                      {/* Status Header */}
                      <div className="flex items-center justify-between pb-4 border-b">
                        <div>
                          <h3 className="text-lg font-medium text-slate-900">
                            {selectedIOState.infeed.device_name}
                          </h3>
                          <p className="text-sm text-slate-600">
                            Last Update: {(() => {
                              console.log(`[DEBUG] Infeed last_update:`, selectedIOState.infeed.last_update);
                              return formatLastSeen(selectedIOState.infeed.last_update);
                            })()}
                          </p>
                        </div>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                          getStatusBadgeColor(selectedIOState.infeed.mqtt_status)
                        }`}>
                          MQTT: {selectedIOState.infeed.mqtt_status}
                        </span>
                      </div>

                      {/* Infeed States - Compact Layout matching Outfeed */}
                      {selectedIOState.infeed.states && (
                        <div className="grid grid-cols-2 gap-6">
                          {/* Left Column - Weight Scale Only */}
                          <div>
                            {selectedIOState.infeed.weight_scale ? (
                              <div>
                                <h4 className="text-sm font-medium text-slate-700 mb-2">
                                  Weight Scale
                                </h4>
                                <div className="grid grid-cols-3 gap-2 mb-2">
                                  <div className="bg-blue-50 rounded p-2">
                                    <div className="text-xs text-blue-600">Weight</div>
                                    <div className="text-sm font-bold text-blue-900">
                                      {convertWeight(selectedIOState.infeed.weight_scale.weight)}
                                    </div>
                                  </div>
                                  <div className="bg-purple-50 rounded p-2">
                                    <div className="text-xs text-purple-600">Raw Signal</div>
                                    <div className="text-sm font-bold text-purple-900">
                                      {selectedIOState.infeed.weight_scale.adc_raw.toLocaleString()}
                                    </div>
                                  </div>
                                  <div className="bg-green-50 rounded p-2">
                                    <div className="text-xs text-green-600">State</div>
                                    <div className="text-sm font-bold text-green-900">
                                      {selectedIOState.infeed.weight_scale.state}
                                    </div>
                                  </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2 mb-2">
                                  <div className="bg-gray-100 rounded p-2">
                                    <div className="text-xs text-gray-600">Zero Point</div>
                                    <div className="text-sm font-bold text-gray-900">
                                      {selectedIOState.infeed.weight_scale.zero_adc?.toLocaleString() || '0'}
                                    </div>
                                  </div>
                                  <div className="bg-gray-100 rounded p-2">
                                    <div className="text-xs text-gray-600">Calibration Point</div>
                                    <div className="text-sm font-bold text-gray-900">
                                      {selectedIOState.infeed.weight_scale.calibration_adc?.toLocaleString() || '0'}
                                    </div>
                                  </div>
                                  <div className="bg-orange-50 rounded p-2">
                                    <div className="text-xs text-orange-600">Calibration State</div>
                                    <div className="text-sm font-bold text-orange-900">
                                      {selectedIOState.infeed.weight_scale.calibration_state || 'UNKNOWN'}
                                    </div>
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div className="bg-yellow-50 rounded p-2">
                                    <div className="text-xs text-yellow-600">Load Cell Voltage</div>
                                    <div className="text-sm font-bold text-yellow-900">
                                      {selectedIOState.infeed.weight_scale.excitation_voltage?.toFixed(2) || '0.00'}V
                                    </div>
                                  </div>
                                  <div className="bg-indigo-50 rounded p-2">
                                    <div className="text-xs text-indigo-600">Load Cell Current</div>
                                    <div className="text-sm font-bold text-indigo-900">
                                      {selectedIOState.infeed.weight_scale.excitation_current?.toFixed(2) || '0.00'}mA
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div>
                                <h4 className="text-sm font-medium text-slate-700 mb-2">Weight Scale</h4>
                                <div className="text-sm text-gray-500">No scale data available</div>
                              </div>
                            )}
                          </div>

                          {/* Right Column - All Other Controls in 2x2 Grid */}
                          <div className="grid grid-cols-2 gap-4">
                            {/* Machine Controls */}
                            <div>
                              <h4 className="text-sm font-medium text-slate-700 mb-2">Machine Controls</h4>
                              <div className="space-y-2">
                                <button 
                                  className={`w-full px-3 py-2 rounded text-xs font-medium transition-colors ${
                                    selectedIOState.infeed.states.infeed_conveyor === 'ON'
                                      ? 'bg-green-500 text-white hover:bg-green-600'
                                      : 'bg-red-500 text-white hover:bg-red-600'
                                  }`}
                                  title="Infeed Conveyor"
                                  disabled
                                >
                                  <div className="flex items-center justify-between">
                                    <span>Conveyor</span>
                                    <span className="font-bold">{selectedIOState.infeed.states.infeed_conveyor}</span>
                                  </div>
                                </button>
                                <button 
                                  className={`w-full px-3 py-2 rounded text-xs font-medium transition-colors ${
                                    selectedIOState.infeed.states.buffer_hopper_state === 'OPEN'
                                      ? 'bg-blue-500 text-white hover:bg-blue-600'
                                      : 'bg-orange-500 text-white hover:bg-orange-600'
                                  }`}
                                  title="Buffer Hopper"
                                  disabled
                                >
                                  <div className="flex items-center justify-between">
                                    <span>Buffer</span>
                                    <span className="font-bold">{selectedIOState.infeed.states.buffer_hopper_state}</span>
                                  </div>
                                </button>
                                <button 
                                  className={`w-full px-3 py-2 rounded text-xs font-medium transition-colors ${
                                    selectedIOState.infeed.states.weigh_hopper_state === 'OPEN'
                                      ? 'bg-blue-500 text-white hover:bg-blue-600'
                                      : 'bg-orange-500 text-white hover:bg-orange-600'
                                  }`}
                                  title="Weigh Hopper"
                                  disabled
                                >
                                  <div className="flex items-center justify-between">
                                    <span>Weigh</span>
                                    <span className="font-bold">{selectedIOState.infeed.states.weigh_hopper_state}</span>
                                  </div>
                                </button>
                              </div>
                            </div>

                            {/* Device Status */}
                            <div>
                              <h4 className="text-sm font-medium text-slate-700 mb-2">Device Status</h4>
                              <div className="space-y-2">
                                <button 
                                  className={`w-full px-3 py-2 rounded text-xs font-medium transition-colors ${
                                    selectedIOState.infeed.states.estop === 'OFF'
                                      ? 'bg-green-500 text-white hover:bg-green-600'
                                      : 'bg-red-600 text-white hover:bg-red-700 animate-pulse'
                                  }`}
                                  title="Emergency Stop"
                                  disabled
                                >
                                  <div className="flex items-center justify-between">
                                    <span>ESTOP</span>
                                    <span className="font-bold">{selectedIOState.infeed.states.estop === 'OFF' ? 'NORMAL' : 'ACTIVE'}</span>
                                  </div>
                                </button>
                                <button 
                                  className={`w-full px-3 py-2 rounded text-xs font-medium transition-colors ${
                                    (() => {
                                      const calibrationMode = selectedIOState.infeed.states.calibration_mode;
                                      const lineStatus = selectedIOState.infeed.states.line_production_status || selectedIOState.infeed.states.line_status;
                                      
                                      if (calibrationMode === 'ON') {
                                        return 'bg-yellow-500 text-white hover:bg-yellow-600';
                                      } else if (lineStatus === 'PRODUCTION') {
                                        return 'bg-green-500 text-white hover:bg-green-600';
                                      } else {
                                        return 'bg-gray-500 text-white hover:bg-gray-600';
                                      }
                                    })()
                                  }`}
                                  title="Device Operational Status"
                                  disabled
                                >
                                  <div className="flex items-center justify-between">
                                    <span>Status</span>
                                    <span className="font-bold">
                                      {(() => {
                                        const calibrationMode = selectedIOState.infeed.states.calibration_mode;
                                        const calibrationStep = selectedIOState.infeed.states.calibration_step;
                                        const lineStatus = selectedIOState.infeed.states.line_production_status || selectedIOState.infeed.states.line_status;
                                        
                                        if (calibrationMode === 'ON') {
                                          if (calibrationStep === 'ZERO_SET') {
                                            return 'CALIBRATION-ZERO-SET';
                                          } else if (calibrationStep === 'SPAN_SET') {
                                            return 'CALIBRATION-COMPLETE';
                                          } else {
                                            return 'CALIBRATION-ACTIVE';
                                          }
                                        } else {
                                          return lineStatus || 'STOP';
                                        }
                                      })()}
                                    </span>
                                  </div>
                                </button>
                              </div>
                            </div>

                            {/* Calibration Indicators */}
                            <div>
                              <h4 className="text-sm font-medium text-slate-700 mb-2">Calibration Status</h4>
                              <div className="space-y-2">
                                {/* Light indicators */}
                                <div className="flex space-x-2">
                                  <button 
                                    className={`flex-1 px-3 py-2 rounded text-xs font-medium transition-colors ${
                                      selectedIOState.infeed.states.red_light === 'ON'
                                        ? 'bg-red-600 text-white'
                                        : selectedIOState.infeed.states.red_light === 'FLASH'
                                        ? 'bg-red-600 text-white animate-pulse'
                                        : 'bg-gray-200 text-gray-600'
                                    }`}
                                    title="Red Light"
                                    disabled
                                  >
                                    <div className="flex flex-col items-center">
                                      <span className="text-lg">🔴</span>
                                      <span className="mt-1">{selectedIOState.infeed.states.red_light || 'OFF'}</span>
                                    </div>
                                  </button>
                                  <button 
                                    className={`flex-1 px-3 py-2 rounded text-xs font-medium transition-colors ${
                                      selectedIOState.infeed.states.yellow_light === 'ON'
                                        ? 'bg-yellow-500 text-white'
                                        : selectedIOState.infeed.states.yellow_light === 'FLASH'
                                        ? 'bg-yellow-500 text-white animate-pulse'
                                        : 'bg-gray-200 text-gray-600'
                                    }`}
                                    title="Yellow Light"
                                    disabled
                                  >
                                    <div className="flex flex-col items-center">
                                      <span className="text-lg">🟡</span>
                                      <span className="mt-1">{selectedIOState.infeed.states.yellow_light || 'OFF'}</span>
                                    </div>
                                  </button>
                                  <button 
                                    className={`flex-1 px-3 py-2 rounded text-xs font-medium transition-colors ${
                                      selectedIOState.infeed.states.green_light === 'ON'
                                        ? 'bg-green-600 text-white'
                                        : selectedIOState.infeed.states.green_light === 'FLASH'
                                        ? 'bg-green-600 text-white animate-pulse'
                                        : 'bg-gray-200 text-gray-600'
                                    }`}
                                    title="Green Light"
                                    disabled
                                  >
                                    <div className="flex flex-col items-center">
                                      <span className="text-lg">🟢</span>
                                      <span className="mt-1">{selectedIOState.infeed.states.green_light || 'OFF'}</span>
                                    </div>
                                  </button>
                                </div>
                                {/* Calibration button */}
                                <button 
                                  className={`w-full px-3 py-2 rounded text-xs font-medium transition-colors ${
                                    selectedIOState.infeed.states.calibration_button === 'PRESSED'
                                      ? 'bg-blue-600 text-white shadow-inner'
                                      : 'bg-gray-200 text-gray-600'
                                  }`}
                                  title="Calibration Button"
                                  disabled
                                >
                                  <div className="flex items-center justify-center">
                                    <span className="mr-2">👆</span>
                                    <span>Calibration Button: {selectedIOState.infeed.states.calibration_button || 'OFF'}</span>
                                  </div>
                                </button>
                              </div>
                            </div>

                            {/* Station States - First 5 stations */}
                            <div>
                              <h4 className="text-sm font-medium text-slate-700 mb-2">Stations 1-5</h4>
                              <div className="space-y-1">
                                {Object.entries(selectedIOState.infeed.stations || {})
                                  .slice(0, 5)
                                  .map(([stationNum, states]) => (
                                    <div key={stationNum} className="bg-slate-50 rounded p-2">
                                      <div className="text-xs font-semibold text-slate-700 mb-1">Station {stationNum}</div>
                                      <div className="grid grid-cols-2 gap-1">
                                        <button 
                                          className={`px-2 py-1 rounded text-xs ${
                                            states.product_sensor === 'ON' 
                                              ? 'bg-green-500 text-white' 
                                              : 'bg-gray-200 text-gray-600'
                                          }`}
                                          disabled
                                        >
                                          {states.product_sensor === 'ON' ? 'PRODUCT' : 'CLEAR'}
                                        </button>
                                        <button 
                                          className={`px-2 py-1 rounded text-xs ${
                                            states.arm === 'OPEN' 
                                              ? 'bg-blue-500 text-white' 
                                              : 'bg-orange-500 text-white'
                                          }`}
                                          disabled
                                        >
                                          {states.arm}
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            </div>

                            {/* Station States - Remaining stations */}
                            <div>
                              <h4 className="text-sm font-medium text-slate-700 mb-2">Stations 6-10</h4>
                              <div className="space-y-1">
                                {Object.entries(selectedIOState.infeed.stations || {})
                                  .slice(5, 10)
                                  .map(([stationNum, states]) => (
                                    <div key={stationNum} className="bg-slate-50 rounded p-2">
                                      <div className="text-xs font-semibold text-slate-700 mb-1">Station {stationNum}</div>
                                      <div className="grid grid-cols-2 gap-1">
                                        <button 
                                          className={`px-2 py-1 rounded text-xs ${
                                            states.product_sensor === 'ON' 
                                              ? 'bg-green-500 text-white' 
                                              : 'bg-gray-200 text-gray-600'
                                          }`}
                                          disabled
                                        >
                                          {states.product_sensor === 'ON' ? 'PRODUCT' : 'CLEAR'}
                                        </button>
                                        <button 
                                          className={`px-2 py-1 rounded text-xs ${
                                            states.arm === 'OPEN' 
                                              ? 'bg-blue-500 text-white' 
                                              : 'bg-orange-500 text-white'
                                          }`}
                                          disabled
                                        >
                                          {states.arm}
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <p className="text-lg mb-2">No Infeed Hardware Configured</p>
                      <p className="text-sm">{selectedIOState.infeed.message}</p>
                    </div>
                  )}
                </div>
              ) : (
                // Outfeed States
                <div>
                  {selectedIOState.outfeed.configured ? (
                    <div className="space-y-4">
                      {selectedIOState.outfeed.devices?.map((device, deviceIndex) => {
                        console.log(`[DEBUG] Outfeed device ${deviceIndex} full object:`, device);
                        return (
                        <div key={deviceIndex} className="border border-slate-200 rounded-lg p-4">
                          {/* Device Header */}
                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <h3 className="text-lg font-medium text-slate-900">
                                {device.device_name} (Stations {device.station_range})
                              </h3>
                              <p className="text-sm text-slate-600">
                                Last Update: {(() => {
                                  console.log(`[DEBUG] Outfeed device ${device.device_name} last_update:`, device.last_update);
                                  return formatLastSeen(device.last_update);
                                })()}
                              </p>
                            </div>
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                              getStatusBadgeColor(device.mqtt_status)
                            }`}>
                              MQTT: {device.mqtt_status}
                            </span>
                          </div>


                          {(() => {
                            // Use station_config if available, otherwise extract from station_range
                            let stationConfig = device.station_config || 1; // Use explicit config if available
                            
                            // Fallback: parse station_range if station_config not available
                            if (!device.station_config) {
                              if (device.station_range.includes('5-8')) {
                                stationConfig = 2;
                              } else if (device.station_range.includes('9-10') || device.station_range.includes('9-12')) {
                                stationConfig = 3;
                              } else if (device.station_range.includes('1-4')) {
                                stationConfig = 1;
                              }
                            }
                            
                            const validStations = getValidStationsForConfig(stationConfig);
                            const currentActiveTab = activeStationTab[deviceIndex] || validStations[0];
                            
                            return (
                              <div>
                                {/* Station Tab Navigation */}
                                <div className="border-b border-slate-200 mb-4">
                                  <div className="flex space-x-6">
                                    {validStations.map(stationNum => (
                                      <button
                                        key={stationNum}
                                        onClick={() => setActiveStationTab(prev => ({...prev, [deviceIndex]: stationNum}))}
                                        className={`pb-2 px-1 text-sm font-medium border-b-2 transition-colors ${
                                          currentActiveTab === stationNum
                                            ? 'text-indigo-600 border-indigo-600'
                                            : 'text-slate-500 border-transparent hover:text-slate-700'
                                        }`}
                                      >
                                        Station {stationNum}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                                
                                {/* Station-specific content */}
                                <div className="mb-4 p-3 bg-slate-50 rounded-lg">
                                  <h4 className="text-sm font-medium text-slate-700 mb-1">Station {currentActiveTab} Configuration</h4>
                                  <p className="text-xs text-slate-600">
                                    Viewing controls for Station {currentActiveTab} on {device.device_name}
                                  </p>
                                  
                                  {/* Per-Station Status */}
                                  <div className="mt-3 flex items-center">
                                    <span className="text-sm font-medium text-slate-700 mr-2">Station Status:</span>
                                    <span className={`px-3 py-1 rounded text-sm font-medium ${
                                      device.station_states?.[currentActiveTab]?.status === 'PRODUCTION' 
                                        ? 'bg-green-100 text-green-800'
                                        : device.station_states?.[currentActiveTab]?.status?.startsWith('CALIBRATION')
                                        ? 'bg-yellow-100 text-yellow-800'
                                        : 'bg-gray-100 text-gray-800'
                                    }`}>
                                      {device.station_states?.[currentActiveTab]?.status || 'STOP'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}

                          {/* Device States - Weight Scale on left, everything else on right */}
                          <div className="grid grid-cols-2 gap-6">
                            {/* Left Column - Weight Scale Only */}
                            <div>
                              {(() => {
                                // Determine station configuration
                                let stationConfig = device.station_config || 1;
                                if (!device.station_config) {
                                  if (device.station_range.includes('5-8')) {
                                    stationConfig = 2;
                                  } else if (device.station_range.includes('9-10') || device.station_range.includes('9-12')) {
                                    stationConfig = 3;
                                  } else if (device.station_range.includes('1-4')) {
                                    stationConfig = 1;
                                  }
                                }
                                const validStations = getValidStationsForConfig(stationConfig);
                                
                                // Get the current station's weight data
                                const currentActiveTab = activeStationTab[deviceIndex] || validStations[0];
                                const stationWeightData = device.station_weights?.[currentActiveTab];
                                const hasPerStationData = stationWeightData !== undefined;
                                
                                // Use per-station data if available, otherwise fall back to legacy data
                                const weightData = hasPerStationData ? stationWeightData : device.weight_scale;
                                
                                if (weightData) {
                                  return (
                                    <div>
                                      <h4 className="text-sm font-medium text-slate-700 mb-2">
                                        Station {currentActiveTab} Weight Scale
                                      </h4>
                                      
                                      {/* Calibration is performed from the Calibration page, not here */}
                                      <div className="grid grid-cols-3 gap-2 mb-2">
                                        <div className="bg-blue-50 rounded p-2">
                                          <div className="text-xs text-blue-600">Weight</div>
                                          <div className="text-sm font-bold text-blue-900">
                                            {convertWeight(weightData.weight)}
                                          </div>
                                        </div>
                                        <div className="bg-purple-50 rounded p-2">
                                          <div className="text-xs text-purple-600">Raw Signal</div>
                                          <div className="text-sm font-bold text-purple-900">
                                            {weightData.adc_raw?.toLocaleString() || '0'}
                                          </div>
                                        </div>
                                        <div className="bg-green-50 rounded p-2">
                                          <div className="text-xs text-green-600">State</div>
                                          <div className="text-sm font-bold text-green-900">
                                            {weightData.state || 'IDLE'}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="grid grid-cols-3 gap-2 mb-2">
                                        <div className="bg-gray-100 rounded p-2">
                                          <div className="text-xs text-gray-600">Zero Point</div>
                                          <div className="text-sm font-bold text-gray-900">
                                            {weightData.zero_adc?.toLocaleString() || '0'}
                                          </div>
                                        </div>
                                        <div className="bg-gray-100 rounded p-2">
                                          <div className="text-xs text-gray-600">Calibration Point</div>
                                          <div className="text-sm font-bold text-gray-900">
                                            {weightData.calibration_adc?.toLocaleString() || '0'}
                                          </div>
                                        </div>
                                        <div className="bg-orange-50 rounded p-2">
                                          <div className="text-xs text-orange-600">Calibration State</div>
                                          <div className="text-sm font-bold text-orange-900">
                                            {weightData.calibration_state || 'UNKNOWN'}
                                          </div>
                                        </div>
                                      </div>
                                      <div className="grid grid-cols-2 gap-2">
                                        <div className="bg-yellow-50 rounded p-2">
                                          <div className="text-xs text-yellow-600">Load Cell Voltage</div>
                                          <div className="text-sm font-bold text-yellow-900">
                                            {weightData.excitation_voltage?.toFixed(2) || '0.00'}V
                                          </div>
                                        </div>
                                        <div className="bg-indigo-50 rounded p-2">
                                          <div className="text-xs text-indigo-600">Load Cell Current</div>
                                          <div className="text-sm font-bold text-indigo-900">
                                            {weightData.excitation_current?.toFixed(2) || '0.00'}mA
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                } else {
                                  return (
                                    <div>
                                      <h4 className="text-sm font-medium text-slate-700 mb-2">
                                        Station {currentActiveTab} Weight Scale
                                      </h4>
                                      
                                      {/* Calibration is performed from the Calibration page, not here */}
                                      <div className="text-sm text-gray-500">No scale data available</div>
                                    </div>
                                  );
                                }
                              })()}
                            </div>

                            {/* Right Column - All Other Controls in 2x2 Grid */}
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                              <h4 className="text-sm font-medium text-slate-700 mb-2">Station Controls</h4>
                              <div className="space-y-2">
                                {(() => {
                                  // Determine station configuration
                                  let stationConfig = device.station_config || 1;
                                  if (!device.station_config) {
                                    if (device.station_range.includes('5-8')) {
                                      stationConfig = 2;
                                    } else if (device.station_range.includes('9-10') || device.station_range.includes('9-12')) {
                                      stationConfig = 3;
                                    } else if (device.station_range.includes('1-4')) {
                                      stationConfig = 1;
                                    }
                                  }
                                  const validStations = getValidStationsForConfig(stationConfig);
                                  
                                  const currentActiveTab = activeStationTab[deviceIndex] || validStations[0];
                                  const stationStates = device.station_states?.[currentActiveTab];
                                  const states = stationStates || device.states;
                                  
                                  return (
                                    <>
                                      <button 
                                        className={`w-full px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-between ${
                                          states.active_button === 'ON'
                                            ? 'bg-green-500 text-white hover:bg-green-600'
                                            : 'bg-gray-500 text-white hover:bg-gray-600'
                                        }`}
                                        title="Active Button - Click to override (coming soon)"
                                        disabled
                                      >
                                        <span>Active Button</span>
                                        <span className="font-bold">{states.active_button}</span>
                                      </button>
                                      <button 
                                        className={`w-full px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-between ${
                                          states.weigh_hopper_door === 'OPEN'
                                            ? 'bg-blue-500 text-white hover:bg-blue-600'
                                            : 'bg-orange-500 text-white hover:bg-orange-600'
                                        }`}
                                        title="Weigh Hopper Door - Click to override (coming soon)"
                                        disabled
                                      >
                                        <span>Weigh Door</span>
                                        <span className="font-bold">{states.weigh_hopper_door}</span>
                                      </button>
                                    </>
                                  );
                                })()}
                              </div>
                            </div>

                            <div>
                              <h4 className="text-sm font-medium text-slate-700 mb-2">Station Lights</h4>
                              <div className="grid grid-cols-3 gap-2">
                                {(() => {
                                  // Determine station configuration
                                  let stationConfig = device.station_config || 1;
                                  if (!device.station_config) {
                                    if (device.station_range.includes('5-8')) {
                                      stationConfig = 2;
                                    } else if (device.station_range.includes('9-10') || device.station_range.includes('9-12')) {
                                      stationConfig = 3;
                                    } else if (device.station_range.includes('1-4')) {
                                      stationConfig = 1;
                                    }
                                  }
                                  const validStations = getValidStationsForConfig(stationConfig);
                                  
                                  const currentActiveTab = activeStationTab[deviceIndex] || validStations[0];
                                  const stationStates = device.station_states?.[currentActiveTab];
                                  const states = stationStates || device.states;
                                  
                                  return (
                                    <>
                                      <button 
                                        className={`py-3 px-2 rounded-md font-medium transition-all ${
                                          states.red_light === 'ON' 
                                            ? 'bg-red-500 text-white shadow-lg shadow-red-500/50 hover:bg-red-600' 
                                            : 'bg-gray-300 text-gray-600 hover:bg-gray-400'
                                        }`}
                                        title="Red Light - Click to override (coming soon)"
                                        disabled
                                      >
                                        <div className="text-xs">RED</div>
                                        <div className="text-xs font-bold mt-1">{states.red_light}</div>
                                      </button>
                                      <button 
                                        className={`py-3 px-2 rounded-md font-medium transition-all ${
                                          states.yellow_light === 'ON' 
                                            ? 'bg-yellow-500 text-white shadow-lg shadow-yellow-500/50 hover:bg-yellow-600' 
                                            : 'bg-gray-300 text-gray-600 hover:bg-gray-400'
                                        }`}
                                        title="Yellow Light - Click to override (coming soon)"
                                        disabled
                                      >
                                        <div className="text-xs">YELLOW</div>
                                        <div className="text-xs font-bold mt-1">{states.yellow_light}</div>
                                      </button>
                                      <button 
                                        className={`py-3 px-2 rounded-md font-medium transition-all ${
                                          states.green_light === 'ON' 
                                            ? 'bg-green-500 text-white shadow-lg shadow-green-500/50 hover:bg-green-600' 
                                            : 'bg-gray-300 text-gray-600 hover:bg-gray-400'
                                        }`}
                                        title="Green Light - Click to override (coming soon)"
                                        disabled
                                      >
                                        <div className="text-xs">GREEN</div>
                                        <div className="text-xs font-bold mt-1">{states.green_light}</div>
                                      </button>
                                    </>
                                  );
                                })()}
                              </div>
                            </div>

                            {/* Pocket Doors - Third position */}
                            <div>
                              <h4 className="text-sm font-medium text-slate-700 mb-2">Pocket Doors</h4>
                              <div className="grid grid-cols-2 gap-2">
                                {(() => {
                                  // Determine station configuration
                                  let stationConfig = device.station_config || 1;
                                  if (!device.station_config) {
                                    if (device.station_range.includes('5-8')) {
                                      stationConfig = 2;
                                    } else if (device.station_range.includes('9-10') || device.station_range.includes('9-12')) {
                                      stationConfig = 3;
                                    } else if (device.station_range.includes('1-4')) {
                                      stationConfig = 1;
                                    }
                                  }
                                  const validStations = getValidStationsForConfig(stationConfig);
                                  
                                  const currentActiveTab = activeStationTab[deviceIndex] || validStations[0];
                                  const stationStates = device.station_states?.[currentActiveTab];
                                  const states = stationStates || device.states;
                                  
                                  return (
                                    <>
                                      <button 
                                        className={`px-3 py-2 rounded-md text-xs font-medium transition-colors flex items-center justify-between ${
                                          states.pocket_door_1 === 'OPEN'
                                            ? 'bg-blue-500 text-white hover:bg-blue-600'
                                            : 'bg-orange-500 text-white hover:bg-orange-600'
                                        }`}
                                        title="Pocket Door 1 - Click to override (coming soon)"
                                        disabled
                                      >
                                        <span>Pocket 1</span>
                                        <span className="font-bold">{states.pocket_door_1}</span>
                                      </button>
                                      <button 
                                        className={`px-3 py-2 rounded-md text-xs font-medium transition-colors flex items-center justify-between ${
                                          states.pocket_door_2 === 'OPEN'
                                            ? 'bg-blue-500 text-white hover:bg-blue-600'
                                            : 'bg-orange-500 text-white hover:bg-orange-600'
                                        }`}
                                        title="Pocket Door 2 - Click to override (coming soon)"
                                        disabled
                                      >
                                        <span>Pocket 2</span>
                                        <span className="font-bold">{states.pocket_door_2}</span>
                                      </button>
                                      <button 
                                        className={`px-3 py-2 rounded-md text-xs font-medium transition-colors flex items-center justify-between ${
                                          states.pocket_door_3 === 'OPEN'
                                            ? 'bg-blue-500 text-white hover:bg-blue-600'
                                            : 'bg-orange-500 text-white hover:bg-orange-600'
                                        }`}
                                        title="Pocket Door 3 - Click to override (coming soon)"
                                        disabled
                                      >
                                        <span>Pocket 3</span>
                                        <span className="font-bold">{states.pocket_door_3}</span>
                                      </button>
                                      <button 
                                        className={`px-3 py-2 rounded-md text-xs font-medium transition-colors flex items-center justify-between ${
                                          states.pocket_door_4 === 'OPEN'
                                            ? 'bg-blue-500 text-white hover:bg-blue-600'
                                            : 'bg-orange-500 text-white hover:bg-orange-600'
                                        }`}
                                        title="Pocket Door 4 - Click to override (coming soon)"
                                        disabled
                                      >
                                        <span>Pocket 4</span>
                                        <span className="font-bold">{states.pocket_door_4}</span>
                                      </button>
                                    </>
                                  );
                                })()}
                              </div>
                            </div>

                            {/* Infeed Call For Product Section - Fourth position */}
                            <div>
                              <h4 className="text-sm font-medium text-slate-700 mb-2">Infeed Call For Product</h4>
                              <div className="space-y-2">
                                {(() => {
                                  // Determine station configuration
                                  let stationConfig = device.station_config || 1;
                                  if (!device.station_config) {
                                    if (device.station_range.includes('5-8')) {
                                      stationConfig = 2;
                                    } else if (device.station_range.includes('9-10') || device.station_range.includes('9-12')) {
                                      stationConfig = 3;
                                    } else if (device.station_range.includes('1-4')) {
                                      stationConfig = 1;
                                    }
                                  }
                                  const validStations = getValidStationsForConfig(stationConfig);
                                  
                                  const currentActiveTab = activeStationTab[deviceIndex] || validStations[0];
                                  const stationStates = device.station_states?.[currentActiveTab];
                                  const states = stationStates || device.states;
                                  
                                  return (
                                    <>
                                      <button 
                                        className={`w-full px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-between ${
                                          states.call_for_infeed_button === 'ON'
                                            ? 'bg-purple-500 text-white hover:bg-purple-600'
                                            : 'bg-gray-500 text-white hover:bg-gray-600'
                                        }`}
                                        title="Call For Infeed Product Button - Click to override (coming soon)"
                                        disabled
                                      >
                                        <span>Call Button</span>
                                        <span className="font-bold">{states.call_for_infeed_button || 'OFF'}</span>
                                      </button>
                                      <div 
                                        className={`w-full px-3 py-2 rounded-md text-sm font-medium transition-all flex items-center justify-between ${
                                          states.call_for_infeed_blue_light === 'ON' 
                                            ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/50' 
                                            : 'bg-gray-300 text-gray-600'
                                        }`}
                                        title="Call For Infeed Blue Light Indicator"
                                      >
                                        <span>Blue Light</span>
                                        <div className="flex items-center gap-2">
                                          <div className={`w-3 h-3 rounded-full ${
                                            states.call_for_infeed_blue_light === 'ON' 
                                              ? 'bg-white animate-pulse' 
                                              : 'bg-gray-500'
                                          }`}></div>
                                          <span className="font-bold">{states.call_for_infeed_blue_light || 'OFF'}</span>
                                        </div>
                                      </div>
                                    </>
                                  );
                                })()}
                              </div>
                            </div>
                            </div>
                          </div>
                        </div>
                      );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <p className="text-lg mb-2">No Outfeed Hardware Configured</p>
                      <p className="text-sm">{selectedIOState.outfeed.message}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default IODiagnostics;